import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { message } from '@surecompanies/core_components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { financeService } from '@hixme/api'
import { PaymentTypeEnum } from 'types/benefit'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { normalizeVcnProvider } from 'modules/virtual-cards/normalizeVcnProvider'
import {
  useVcnProviderAvailability,
  VCN_PROVIDER_NAMES,
} from 'modules/virtual-cards/useVcnProviderAvailability'
import { mapPartnerVcnWexShowPanToFinanceVirtualCard } from 'modules/virtual-cards/mapWexVcnProvisionToFinanceVirtualCard'
import { getPartnerVcnWexShowPan } from 'partner-payment-service/api/vcn'
import { getWexVirtualCardsByBenefitId } from 'enrollment-platform/api/vcn'
import { useBenefit } from 'modules/benefits/hooks/useBenefit'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type { UpdateBenefitRequestDto } from 'modules/benefits/dtos/Benefit.dto'
import { joinNameParts } from '../../Utils/payment-assignment.helper'
import type {
  VirtualCardData,
  VcnAction,
  VcnPaymentManagementProps,
} from '../VcnPaymentManagement.types'
import { useVcnActions } from './useVcnActions'

/**
 * Check if a card is in an active (non-terminated) state.
 * Active states: ACTIVE, SUSPENDED
 * Terminated states: TERMINATED, CLOSED, etc.
 */
const isCardActive = (card: VirtualCardData | null | undefined): boolean => {
  if (!card) return false
  const state = (card.state || card.status || '').toUpperCase()
  return ['ACTIVE', 'SUSPENDED'].includes(state)
}

const ALL_PRIMARY_PAYMENT_METHOD_OPTIONS = [
  { label: PaymentTypeEnum.ACH, value: PaymentTypeEnum.ACH },
  { label: PaymentTypeEnum.VCN, value: PaymentTypeEnum.VCN },
  { label: PaymentTypeEnum.CHECK, value: PaymentTypeEnum.CHECK },
  { label: PaymentTypeEnum.GROUP_ACH, value: PaymentTypeEnum.GROUP_ACH },
]

export interface UsePaymentManagementOptions {
  paymentCase: VcnPaymentManagementProps['paymentCase']
  canUpdateCase?: boolean
  onComplete?: () => void
}

export function usePaymentManagement({
  paymentCase,
  canUpdateCase = false,
  onComplete,
}: UsePaymentManagementOptions) {
  const queryClient = useQueryClient()
  const { updateBenefitMutation } = useBenefit()
  const providerAvailability = useVcnProviderAvailability()
  const { flags } = useFeatureFlags()

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived values from paymentCase
  // ─────────────────────────────────────────────────────────────────────────────
  const primaryPaymentMethod =
    paymentCase.carrierApplicationPrimaryPaymentMethod
  const virtualCardType = normalizeVcnProvider(paymentCase.virtualCardType)
  const virtualCardId = paymentCase.virtualCardId?.trim()
  const virtualCardKey = paymentCase.virtualCardKey?.trim()
  const benefitId = paymentCase.benefitId
  const employeePublicKey = paymentCase.benefit?.employeePublicKey
  const personFullName = joinNameParts(
    paymentCase.firstName,
    paymentCase.middleName,
    paymentCase.lastName
  )
  const currentHealthPlanId = paymentCase.healthPlanId

  // ACH details
  const achAccountNumber = paymentCase.carrierApplicationPrimaryACHAccountNumber
  const achAccountType = paymentCase.carrierApplicationPrimaryACHAccountType
  const achAccountUse = paymentCase.carrierApplicationPrimaryACHAccountUse

  // Mailing address
  const mailingAddress = useMemo(
    () =>
      [
        paymentCase.mailingStreetAddress,
        paymentCase.mailingCity,
        paymentCase.mailingStateProvince,
        paymentCase.mailingPostalCode,
      ]
        .filter(Boolean)
        .join(', '),
    [
      paymentCase.mailingStreetAddress,
      paymentCase.mailingCity,
      paymentCase.mailingStateProvince,
      paymentCase.mailingPostalCode,
    ]
  )

  // Payment method flags
  const isVcnMethod = primaryPaymentMethod === PaymentTypeEnum.VCN
  const isAchMethod = primaryPaymentMethod === PaymentTypeEnum.ACH
  const isCheckMethod = primaryPaymentMethod === PaymentTypeEnum.CHECK
  const isGroupAchMethod = primaryPaymentMethod === PaymentTypeEnum.GROUP_ACH

  // ─────────────────────────────────────────────────────────────────────────────
  // Feature flags
  // ─────────────────────────────────────────────────────────────────────────────
  const enableNoteModalBeforeCreate = Boolean(
    flags['admin.virtual-card.enable-note-modal-before-create']
  )
  const enableTerminateAllBeforeCreate = Boolean(
    flags['admin.virtual-card.enable-terminate-all-before-create']
  )

  // Filter payment method options based on feature flags
  const primaryPaymentMethodOptions = useMemo(() => {
    return ALL_PRIMARY_PAYMENT_METHOD_OPTIONS.filter((opt) => {
      if (opt.value === PaymentTypeEnum.CHECK) {
        return flags['admin.finance.payment-coordination-enable-check']
      }
      if (opt.value === PaymentTypeEnum.GROUP_ACH) {
        return flags['admin.finance.payment-coordination-enable-group-ach']
      }
      return true
    })
  }, [flags])

  // ─────────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>(
    undefined
  )
  const [selectedVcnCard, setSelectedVcnCard] = useState<string | undefined>(
    undefined
  )
  const [selectedAction, setSelectedAction] = useState<VcnAction | undefined>(
    undefined
  )
  const [selectedActionProvider, setSelectedActionProvider] = useState<
    VirtualCardType | undefined
  >(undefined)
  const [isRefetching, setIsRefetching] = useState(false)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [selectedVcnProvider, setSelectedVcnProvider] =
    useState<VirtualCardType | null>(null)
  const [showMarqetaConfirm, setShowMarqetaConfirm] = useState(false)
  const [achAccountNumberInput, setAchAccountNumberInput] = useState('')

  // ─────────────────────────────────────────────────────────────────────────────
  // VCN Actions hook
  // ─────────────────────────────────────────────────────────────────────────────
  const loadAvailableCardsRef = useRef<(() => Promise<void>) | null>(null)

  const {
    generateVcn,
    isGenerating,
    generateVcnWithTerminateAll,
    isTerminatingAll,
    reuseVcn,
    isReusing,
    performAction,
    isPerformingAction,
    availableCards,
    isLoadingAvailableCards,
    loadAvailableCards,
  } = useVcnActions({
    benefitId,
    employeePublicKey,
    personFullName,
    onSuccess: async () => {
      setIsRefetching(true)
      try {
        if (paymentCase?.id != null) {
          await queryClient.refetchQueries({
            queryKey: ['payment-assignment-detail', paymentCase.id],
          })
        }
        await queryClient.invalidateQueries({
          queryKey: ['vcn-payment-management-card', benefitId],
        })
        if (loadAvailableCardsRef.current) {
          await loadAvailableCardsRef.current()
        }
        setSelectedVcnCard(undefined)
        setSelectedAction(undefined)
        setSelectedActionProvider(undefined)
        onComplete?.()
      } finally {
        setIsRefetching(false)
      }
    },
  })

  useEffect(() => {
    loadAvailableCardsRef.current = loadAvailableCards
  }, [loadAvailableCards])

  // ─────────────────────────────────────────────────────────────────────────────
  // Update mutation
  // ─────────────────────────────────────────────────────────────────────────────
  const updateMutation = updateBenefitMutation({
    mutationOptions: {
      onSuccess: async () => {
        message.success('Payment method updated successfully')
        setIsRefetching(true)
        try {
          if (paymentCase?.id != null) {
            await Promise.all([
              queryClient.refetchQueries({
                queryKey: ['payment-assignment-detail', paymentCase.id],
              }),
              queryClient.refetchQueries({
                queryKey: ['payment-assignment-benefit-events', paymentCase.id],
              }),
            ])
          }
          setIsEditing(false)
          onComplete?.()
        } finally {
          setIsRefetching(false)
        }
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          'Failed to update payment method'
        message.error(errorMessage)
      },
    },
  })

  // ─────────────────────────────────────────────────────────────────────────────
  // Virtual card query
  // ─────────────────────────────────────────────────────────────────────────────
  const wexMissingCardId =
    primaryPaymentMethod === PaymentTypeEnum.VCN &&
    virtualCardType === VirtualCardType.WEX &&
    !virtualCardId

  const shouldFetchVcn =
    primaryPaymentMethod === PaymentTypeEnum.VCN &&
    !!benefitId &&
    !isEditing &&
    !wexMissingCardId

  const {
    data: virtualCard,
    isLoading: virtualCardLoading,
    isError: virtualCardError,
  } = useQuery({
    queryKey: [
      'vcn-payment-management-card',
      benefitId,
      virtualCardType,
      virtualCardId ?? '',
    ],
    queryFn: async (): Promise<VirtualCardData | null> => {
      if (virtualCardType === VirtualCardType.WEX) {
        if (!virtualCardId) return null
        const [showPanRes, epsCards] = await Promise.all([
          getPartnerVcnWexShowPan(virtualCardId),
          getWexVirtualCardsByBenefitId(benefitId),
        ])
        const epsRow = epsCards.find((c) => c.merchantLogId === virtualCardId)
        return mapPartnerVcnWexShowPanToFinanceVirtualCard(
          showPanRes,
          epsRow?.status
        ) as VirtualCardData
      }
      const res = await financeService({
        route: `benefit/${benefitId}/virtual-card`,
      })
      const raw = (res?.VirtualCard ?? null) as VirtualCardData | null
      if (!raw) return null
      return { ...raw, vcnProvider: VirtualCardType.MARQETA }
    },
    enabled: shouldFetchVcn,
  })

  // ─────────────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────────────
  // Load available cards when entering edit mode
  useEffect(() => {
    if (isEditing && employeePublicKey) {
      loadAvailableCards()
    }
  }, [isEditing, employeePublicKey, loadAvailableCards])

  // Load available cards in view mode when VCN is selected
  useEffect(() => {
    if (
      !isEditing &&
      primaryPaymentMethod === PaymentTypeEnum.VCN &&
      employeePublicKey &&
      canUpdateCase
    ) {
      loadAvailableCards()
    }
  }, [
    isEditing,
    primaryPaymentMethod,
    employeePublicKey,
    canUpdateCase,
    loadAvailableCards,
    virtualCardId,
  ])

  // Reset form when paymentCase changes
  useEffect(() => {
    setIsEditing(false)
    setSelectedMethod(undefined)
    setSelectedVcnCard(undefined)
    setAchAccountNumberInput('')
  }, [paymentCase?.id])

  // Initialize selected method when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setSelectedMethod(primaryPaymentMethod ?? undefined)
      setAchAccountNumberInput(achAccountNumber ?? '')
    }
  }, [isEditing, primaryPaymentMethod, achAccountNumber])

  // Initialize selected method when component mounts with VCN or ACH
  useEffect(() => {
    if (
      !isEditing &&
      (isVcnMethod || isAchMethod) &&
      selectedMethod === undefined
    ) {
      setSelectedMethod(primaryPaymentMethod ?? undefined)
    }
  }, [
    isEditing,
    isVcnMethod,
    isAchMethod,
    primaryPaymentMethod,
    selectedMethod,
  ])

  // ─────────────────────────────────────────────────────────────────────────────
  // Computed values
  // ─────────────────────────────────────────────────────────────────────────────
  const isSubmitting = updateMutation.isPending
  const isAnyLoading =
    isGenerating ||
    isTerminatingAll ||
    isReusing ||
    isPerformingAction ||
    isSubmitting ||
    isRefetching

  const showVcnDetails =
    !virtualCardLoading &&
    !virtualCardError &&
    !!virtualCard &&
    primaryPaymentMethod === PaymentTypeEnum.VCN

  // Check if there are any existing cards to terminate
  // This includes the current benefit's card OR any available family cards
  const hasExistingCard =
    !!virtualCard?.pan ||
    (Array.isArray(availableCards) && availableCards.length > 0)

  // Check if current card is active (not terminated)
  const isCurrentCardActive = isCardActive(virtualCard)

  // Check if there are any active (non-terminated) cards in available cards
  const hasActiveAvailableCards = useMemo(() => {
    if (!Array.isArray(availableCards) || availableCards.length === 0) {
      return false
    }
    return availableCards.some(isCardActive)
  }, [availableCards])

  // Check if there are any active cards that need to be terminated
  // This is used for showing the warning message
  const hasActiveCardsToTerminate =
    isCurrentCardActive || hasActiveAvailableCards

  // Determine if we need to show the modal
  // Show modal ONLY when terminate-all is enabled AND there are active cards to terminate
  // Note modal alone does NOT trigger popup - it only adds form fields when popup is shown
  const shouldShowModal = useMemo(() => {
    // Only show modal if we need to terminate active cards
    if (enableTerminateAllBeforeCreate && hasActiveCardsToTerminate) return true
    return false
  }, [enableTerminateAllBeforeCreate, hasActiveCardsToTerminate])

  // Should show terminate warning in modal
  const shouldShowTerminateWarning =
    enableTerminateAllBeforeCreate && hasActiveCardsToTerminate

  // When terminate-all is enabled, we must wait for card data to load
  // before allowing generate to prevent skipping cleanup
  const isCardsDataLoading = virtualCardLoading || isLoadingAvailableCards

  // Disable generate when terminate-all is enabled but cards haven't loaded yet
  // This prevents creating new cards while existing cards are still being fetched
  const isGenerateDisabledForTerminateAll =
    enableTerminateAllBeforeCreate && isCardsDataLoading

  const displayMethod = isEditing ? selectedMethod : primaryPaymentMethod
  const isVcnSelected = displayMethod === PaymentTypeEnum.VCN
  const isAchSelected = displayMethod === PaymentTypeEnum.ACH

  const selectedCardForReuse = useMemo(() => {
    if (selectedVcnCard == null) return null
    const cardIndex = parseInt(selectedVcnCard, 10)
    return availableCards[cardIndex] ?? null
  }, [selectedVcnCard, availableCards])

  // Build dropdown menu items for Generate VCN
  const generateVcnMenuItems = useMemo(() => {
    return providerAvailability.enabledProviders.map((provider) => ({
      key: provider,
      label:
        provider === VirtualCardType.WEX
          ? `${VCN_PROVIDER_NAMES[provider]} (default)`
          : `${VCN_PROVIDER_NAMES[provider]} — only if carrier does not accept WEX`,
    }))
  }, [providerAvailability.enabledProviders])

  // Build provider options for Select dropdown
  const providerSelectOptions = useMemo(() => {
    return providerAvailability.enabledProviders.map((provider) => ({
      value: provider,
      label: VCN_PROVIDER_NAMES[provider],
    }))
  }, [providerAvailability.enabledProviders])

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEditClick = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleMethodChange = useCallback(
    (value: string) => {
      setSelectedMethod(value)
      if (value !== primaryPaymentMethod) {
        setIsEditing(true)
        if (value === PaymentTypeEnum.ACH) {
          setAchAccountNumberInput(achAccountNumber ?? '')
        }
      }
    },
    [primaryPaymentMethod, achAccountNumber]
  )

  const handleMethodDropdownOpen = useCallback(
    (open: boolean) => {
      if (open && !isEditing) {
        setIsEditing(true)
      }
    },
    [isEditing]
  )

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setSelectedMethod(primaryPaymentMethod ?? undefined)
    setSelectedVcnCard(undefined)
    setAchAccountNumberInput('')
  }, [primaryPaymentMethod])

  const handleSubmit = useCallback(() => {
    if (!benefitId) {
      message.error('Benefit ID is missing')
      return
    }

    const data: UpdateBenefitRequestDto = {}

    if (
      selectedMethod === PaymentTypeEnum.VCN ||
      selectedMethod === PaymentTypeEnum.CHECK ||
      selectedMethod === PaymentTypeEnum.GROUP_ACH
    ) {
      data.carrierApplicationPrimaryPaymentMethod = selectedMethod
      data.carrierApplicationPrimaryACHAccountNumber = null
      data.carrierApplicationPrimaryACHAccountType = null
      data.carrierApplicationPrimaryACHAccountUse = null
    } else if (selectedMethod === PaymentTypeEnum.ACH) {
      const acctNum = achAccountNumberInput.trim()
      if (!acctNum) {
        message.error('ACH account number is required')
        return
      }
      data.carrierApplicationPrimaryPaymentMethod = PaymentTypeEnum.ACH
      data.carrierApplicationPrimaryACHAccountNumber = acctNum
    }

    if (Object.keys(data).length === 0) {
      message.error('Please select a payment method')
      return
    }

    updateMutation.mutate({ benefitId, data })
  }, [benefitId, selectedMethod, achAccountNumberInput, updateMutation])

  const handleReuseVcn = useCallback(() => {
    if (selectedVcnCard == null) {
      message.error('Please select a VCN to reuse')
      return
    }
    const cardIndex = parseInt(selectedVcnCard, 10)
    const card = availableCards[cardIndex]
    if (!card) {
      message.error('Selected VCN not found')
      return
    }
    // Execute reuse directly without popup
    reuseVcn(card)
  }, [selectedVcnCard, availableCards, reuseVcn])

  const handleGenerateVcnMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const provider = key as VirtualCardType

      // If no modal needed (both flags off or terminate flag on but no active cards)
      // generate directly without popup
      if (!shouldShowModal) {
        if (
          provider === VirtualCardType.MARQETA &&
          providerAvailability.enabledProviders.length > 1
        ) {
          // Show Marqeta confirmation first, then generate directly
          setShowMarqetaConfirm(true)
          return
        }
        // Generate directly without modal
        generateVcn(provider, '')
        return
      }

      // Show modal for confirmation/note
      if (
        provider === VirtualCardType.MARQETA &&
        providerAvailability.enabledProviders.length > 1
      ) {
        setShowMarqetaConfirm(true)
        return
      }
      setSelectedVcnProvider(provider)
      setGenerateModalOpen(true)
    },
    [providerAvailability.enabledProviders, shouldShowModal, generateVcn]
  )

  const handleMarqetaConfirm = useCallback(() => {
    setShowMarqetaConfirm(false)
    // If no modal needed, generate directly
    if (!shouldShowModal) {
      generateVcn(VirtualCardType.MARQETA, '')
      return
    }
    setSelectedVcnProvider(VirtualCardType.MARQETA)
    setGenerateModalOpen(true)
  }, [shouldShowModal, generateVcn])

  const handleMarqetaCancel = useCallback(() => {
    setShowMarqetaConfirm(false)
  }, [])

  const closeGenerateModal = useCallback(() => {
    setGenerateModalOpen(false)
    setSelectedVcnProvider(null)
  }, [])

  const handleGenerateVcnConfirm = useCallback(
    (reason: string, provider: VirtualCardType) => {
      // Only run terminate-all flow if flag is enabled AND there are ACTIVE cards to terminate
      const shouldTerminateAll =
        enableTerminateAllBeforeCreate && hasActiveCardsToTerminate

      if (shouldTerminateAll) {
        generateVcnWithTerminateAll(provider, { reason }).then(() => {
          closeGenerateModal()
        })
      } else {
        generateVcn(provider, reason).then(() => {
          closeGenerateModal()
        })
      }
    },
    [
      generateVcn,
      generateVcnWithTerminateAll,
      enableTerminateAllBeforeCreate,
      hasActiveCardsToTerminate,
      closeGenerateModal,
    ]
  )

  const handleGenerateVcnWithActivityLog = useCallback(
    (
      provider: VirtualCardType,
      options: {
        reason?: string
        reasonCategory?: string
        zendeskTicket?: string
      }
    ) => {
      // Only run terminate-all flow if flag is enabled AND there are ACTIVE cards to terminate
      const shouldTerminateAll =
        enableTerminateAllBeforeCreate && hasActiveCardsToTerminate

      if (shouldTerminateAll) {
        generateVcnWithTerminateAll(provider, options).then(() => {
          closeGenerateModal()
        })
      } else {
        generateVcn(provider, options.reason ?? '', options).then(() => {
          closeGenerateModal()
        })
      }
    },
    [
      generateVcn,
      generateVcnWithTerminateAll,
      enableTerminateAllBeforeCreate,
      hasActiveCardsToTerminate,
      closeGenerateModal,
    ]
  )

  const handleActionSelect = useCallback((action: VcnAction | undefined) => {
    setSelectedAction(action)
    if (action !== 'generate') {
      setSelectedActionProvider(undefined)
    }
  }, [])

  const handleActionSubmit = useCallback(() => {
    if (!selectedAction) {
      message.error('Please select an action')
      return
    }

    if (selectedAction === 'generate') {
      if (!selectedActionProvider) {
        message.error('Please select a VCN provider')
        return
      }
      if (
        selectedActionProvider === VirtualCardType.MARQETA &&
        providerAvailability.enabledProviders.length > 1
      ) {
        setShowMarqetaConfirm(true)
        return
      }
      setSelectedVcnProvider(selectedActionProvider)
      setGenerateModalOpen(true)
      return
    }

    if (!virtualCard) {
      message.error('No virtual card data available')
      return
    }
    performAction(selectedAction, virtualCard)
  }, [
    selectedAction,
    selectedActionProvider,
    virtualCard,
    performAction,
    providerAvailability.enabledProviders,
  ])

  const handleActionCancel = useCallback(() => {
    setSelectedAction(undefined)
    setSelectedActionProvider(undefined)
  }, [])

  // ─────────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────────
  return {
    // Derived values
    primaryPaymentMethod,
    virtualCardType,
    virtualCardKey,
    benefitId,
    personFullName,
    currentHealthPlanId,
    achAccountNumber,
    achAccountType,
    achAccountUse,
    mailingAddress,

    // Payment method flags
    isVcnMethod,
    isAchMethod,
    isCheckMethod,
    isGroupAchMethod,

    // Feature flags
    enableNoteModalBeforeCreate,
    enableTerminateAllBeforeCreate,

    // Options
    primaryPaymentMethodOptions,
    generateVcnMenuItems,
    providerSelectOptions,

    // State
    isEditing,
    selectedMethod,
    selectedVcnCard,
    setSelectedVcnCard,
    selectedAction,
    selectedActionProvider,
    setSelectedActionProvider,
    generateModalOpen,
    selectedVcnProvider,
    showMarqetaConfirm,
    achAccountNumberInput,
    setAchAccountNumberInput,

    // Loading states
    isGenerating,
    isTerminatingAll,
    isReusing,
    isPerformingAction,
    isSubmitting,
    isRefetching,
    isAnyLoading,
    virtualCardLoading,
    isLoadingAvailableCards,

    // Error states
    virtualCardError,
    wexMissingCardId,

    // Data
    virtualCard,
    availableCards,
    selectedCardForReuse,
    providerAvailability,

    // Computed
    showVcnDetails,
    hasExistingCard,
    hasActiveCardsToTerminate,
    shouldShowModal,
    shouldShowTerminateWarning,
    isCardsDataLoading,
    isGenerateDisabledForTerminateAll,
    displayMethod,
    isVcnSelected,
    isAchSelected,
    canUpdateCase,

    // Handlers
    handleEditClick,
    handleMethodChange,
    handleMethodDropdownOpen,
    handleCancelEdit,
    handleSubmit,
    handleReuseVcn,
    handleGenerateVcnMenuClick,
    handleMarqetaConfirm,
    handleMarqetaCancel,
    closeGenerateModal,
    handleGenerateVcnConfirm,
    handleGenerateVcnWithActivityLog,
    handleActionSelect,
    handleActionSubmit,
    handleActionCancel,
  }
}
